import React, { useEffect, useState } from 'react'
import { type StepType, TourProvider, useTour } from '@reactour/tour'
import { tokens, Button, Title, FontIcon } from '@arvoreeducacao/bonsai-ds'
import { type TourBaseProps } from 'screens/admin-v2/reports-v2/onboarding-tour/tour-base'
import { useOttoAiStore } from 'stores/ottoAiStore'
import useResponsive from 'utils/hooks/use-responsive'

const POPOVER_MAX_WIDTH = 460
const ARROW_SIZE = 21
const VIEWPORT_PADDING = 16

const getDesktopPopoverPosition = (): [number, number] => {
  const button = document.getElementById('flora-ai-button')
  if (!button) return [0, 0]
  const rect = button.getBoundingClientRect()
  const buttonCenterX = rect.left + rect.width / 2
  const popoverLeft = Math.min(
    buttonCenterX - POPOVER_MAX_WIDTH + 60,
    window.innerWidth - POPOVER_MAX_WIDTH - VIEWPORT_PADDING
  )
  const clampedLeft = Math.max(VIEWPORT_PADDING, popoverLeft)
  return [clampedLeft, rect.bottom + 20]
}

const getArrowOffset = (): number => {
  const button = document.getElementById('flora-ai-button')
  if (!button) return 220
  const rect = button.getBoundingClientRect()
  const buttonCenterX = rect.left + rect.width / 2
  const [popoverLeft] = getDesktopPopoverPosition()
  return buttonCenterX - popoverLeft - ARROW_SIZE / 2
}

const TourContent: React.FC<TourBaseProps> = ({ title, description }) => {
  const { isDesktop } = useResponsive()
  const { closeTour, openModal } = useOttoAiStore()
  const [arrowOffset, setArrowOffset] = useState(220)

  useEffect(() => {
    if (isDesktop) {
      setArrowOffset(getArrowOffset())
    }
  }, [isDesktop])

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-2">
        {isDesktop && (
          <div
            className="absolute top-0 left-0 w-[21px] h-[21px] bg-white"
            style={{
              transform: `translateX(${arrowOffset}px) translateY(-6px) rotate(45deg)`
            }}
          />
        )}
        <div className="flex items-center justify-end w-full">
          <div className="cursor-pointer" onClick={closeTour}>
            <FontIcon iconName="cancel" size={24} />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Title size={3} className="text-center max-w-[250px]">
            {title}
          </Title>
          <p className="text-gray-700 m-0 text-center text-base max-w-[400px]">
            {description}
          </p>
        </div>
      </div>

      <div className="flex justify-end items-center gap-2">
        <Button
          theme={{ type: 'secondary', size: 'xLarge' }}
          text="Voltar"
          onClick={() => {
            closeTour()
            openModal()
          }}
          fluid={!isDesktop}
        />
        <Button
          theme={{ type: 'primaryLivros', size: 'xLarge' }}
          text="Finalizar"
          onClick={closeTour}
          fluid={!isDesktop}
        />
      </div>
    </div>
  )
}

const Tour = () => {
  const { setSteps, setIsOpen } = useTour()
  const { isDesktop } = useResponsive()
  const { isTourOpen } = useOttoAiStore()

  useEffect(() => {
    const steps: StepType[] = [
      {
        selector: !isDesktop
          ? '[data-testid="bottom-navigation-item-flower-outline"]'
          : '[id="flora-ai-button"]',
        position: isDesktop ? getDesktopPopoverPosition() : 'bottom',
        content: (
          <TourContent
            title="Converse com a Flora quando quiser"
            description="Sempre que precisar do assistente inteligente, basta clicar neste botão para começar a conversar."
          />
        )
      }
    ]
    setSteps(steps)
  }, [setSteps, isDesktop])

  useEffect(() => {
    if (isTourOpen) {
      setIsOpen(true)

      return
    }

    setIsOpen(false)
  }, [setIsOpen, isTourOpen])

  return null
}

const OttoAiSpotlight = ({ children }) => {
  const { isTourOpen } = useOttoAiStore()
  const { isDesktop } = useResponsive()

  return (
    <TourProvider
      key={`otto-ai-spotlight-${isTourOpen}-${!isDesktop}`}
      steps={[]}
      showCloseButton={false}
      showDots={false}
      showPrevNextButtons={false}
      styles={{
        controls: base => ({ ...base, marginTop: 0 }),
        maskArea: (base, style) => ({
          ...base,
          rx: 50,
          y: style?.y + 5,
          x: style?.x + 5,
          width: style?.width - 10,
          height: style?.height - 10,
          fill: '#000'
        }),
        popover: base => ({
          ...base,
          minWidth: 320,
          maxWidth: !isDesktop ? 328 : POPOVER_MAX_WIDTH,
          marginLeft: !isDesktop ? '-20px' : undefined,
          padding: tokens.spacing.insetLarge,
          borderRadius: `${tokens.radius.radius_20}px`,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
        }),
        badge: () => ({
          display: 'none'
        })
      }}
    >
      {children}
      {isTourOpen && <Tour />}
    </TourProvider>
  )
}

export default OttoAiSpotlight
